import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  Building2,
  Map,
  Package,
  Calendar,
  Settings,
  Ban,
  DollarSign,
  BarChart3,
  LogOut,
  Users,
  Layers,
  HelpCircle
} from 'lucide-react';

export default function AdminLayout({ children, title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const navigation = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Properties', path: '/admin/properties', icon: Building2 },
    { name: 'Maps', path: '/admin/maps', icon: Map },
    { name: 'Inventory', path: '/admin/inventory', icon: Package },
    { name: 'Inventory Types', path: '/admin/inventory-types', icon: Layers },
    { name: 'Reservations', path: '/admin/reservations', icon: Calendar },
    { name: 'Blackouts', path: '/admin/blackouts', icon: Ban },
    { name: 'Pricing', path: '/admin/pricing', icon: DollarSign },
    { name: 'Users', path: '/admin/users', icon: Users },
    { name: 'Reports', path: '/admin/reports', icon: BarChart3 },
    { name: 'Settings', path: '/admin/settings', icon: Settings },
    { name: 'Help', path: '/admin/help', icon: HelpCircle },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Sidebar */}
      <aside className="fixed top-0 left-0 h-full w-64 bg-[#1C1917] text-[#FAFAF9] border-r border-stone-800 z-50 flex flex-col">
        <div className="p-6 border-b border-stone-800 flex-shrink-0">
          <div className="flex flex-col items-start">
            <span className="font-serif tracking-wide whitespace-nowrap text-2xl text-[#FAFAF9] mb-2" data-testid="brand-name">Acqualina Resort</span>
            <p className="text-xs text-[#A8A29E]">Staff Portal</p>
          </div>
        </div>

        <nav className="p-4 flex-1 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-sm mb-1 transition-colors ${
                  isActive
                    ? 'bg-[#9F1239] text-white'
                    : 'text-[#A8A29E] hover:bg-stone-800 hover:text-white'
                }`}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="font-medium">{item.name}</span>
              </button>
            );
          })}
        </nav>

        <div className="flex-shrink-0 p-4 border-t border-stone-800">
          <div className="mb-4 px-4">
            <p className="text-sm font-medium text-white">{user?.full_name}</p>
            <p className="text-xs text-[#A8A29E]">{user?.email}</p>
            <p className="text-xs text-[#B45309] mt-1">{user?.role}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start gap-3 text-[#A8A29E] hover:bg-stone-800 hover:text-white"
            data-testid="btn-logout"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="ml-64 p-8">
        <div className="mb-8">
          <h2 className="text-4xl font-serif tracking-tight text-[#1C1917]" data-testid="page-title">
            {title}
          </h2>
        </div>
        {children}
      </div>
    </div>
  );
}
